import { faFileCircleExclamation, faFileCirclePlus, faHome } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useMemo, useRef } from 'react'
import { compareExportedInstances } from '@companion-app/shared/Import.js'
import type { DropdownChoice, DropdownChoiceId } from '@companion-app/shared/Model/Common.js'
import type { ClientImportObject, ClientImportObjectInstance } from '@companion-app/shared/Model/ImportExport.js'
import { ModuleInstanceType } from '@companion-app/shared/Model/Instance.js'
import { ButtonGridHeader, PageNumberPicker, type PageNumberOption } from '~/Buttons/ButtonGridHeader.js'
import { ButtonGridZoomControl } from '~/Buttons/ButtonGridZoomControl.js'
import {
	ButtonInfiniteGrid,
	type ButtonInfiniteGridButtonProps,
	type ButtonInfiniteGridRef,
} from '~/Buttons/ButtonInfiniteGrid.js'
import { ButtonGridIcon, ButtonGridIconBase } from '~/Buttons/GridButtonIcons.js'
import { useGridZoom } from '~/Buttons/GridZoom.js'
import { Button } from '~/Components/Button'
import { Callout } from '~/Components/Callout'
import { SimpleDropdownInputField } from '~/Components/DropdownInputFieldSimple'
import { Grid } from '~/Components/Grid'
import { Table } from '~/Components/Table.js'
import { useHasBeenRendered } from '~/Hooks/useHasBeenRendered.js'
import { usePagePicker } from '~/Hooks/usePagePicker.js'
import { MyErrorBoundary } from '~/Resources/Error'
import { trpc } from '~/Resources/TRPC'
import { useComputed } from '~/Resources/util'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'

interface ImportPageWizardProps {
	snapshot: ClientImportObject
	connectionRemap: Record<string, string | undefined>
	setConnectionRemap: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>
	doImport: (importPageNumber: number, pageNumber: number, connectionRemap: Record<string, string | undefined>) => void
}

export const ImportPageWizard = observer(function ImportPageWizard({
	snapshot,
	connectionRemap,
	setConnectionRemap,
	doImport,
}: ImportPageWizardProps) {
	const { pages, userConfig } = useContext(RootAppStoreContext)

	const isSinglePage = snapshot.type === 'page'

	const [snapshotPageOptions, pageCount] = useMemo(() => {
		if (isSinglePage) {
			const snapshotPageOptions: PageNumberOption[] = [
				{
					value: 1,
					label: snapshot.page?.name ? `1 (${snapshot.page.name})` : '1',
				},
			]
			return [snapshotPageOptions, 1]
		} else {
			const snapshotPageOptions: PageNumberOption[] = []
			let pageCount = 0
			for (const [pageNumber, pageInfo] of Object.entries(snapshot.pages ?? {})) {
				const pageNumberInt = parseInt(pageNumber)
				pageCount = Math.max(pageCount, pageNumberInt)

				snapshotPageOptions.push({
					value: pageNumberInt,
					label: pageInfo.name ? `${pageNumberInt} (${pageInfo.name})` : `${pageNumberInt}`,
				})
			}

			return [snapshotPageOptions, pageCount]
		}
	}, [snapshot.pages, snapshot.page, isSinglePage])

	const { pageNumber, setPageNumber, changePage } = usePagePicker(pages.data.length, 1)
	const {
		pageNumber: importPageNumber,
		setPageNumber: setImportPageNumber,
		changePage: changeImportPage,
	} = usePagePicker(pageCount, 1)

	const setConnectionRemap2 = useCallback(
		(fromId: string, toId: string) => {
			setConnectionRemap((oldRemap) => ({
				...oldRemap,
				[fromId]: toId,
			}))
		},
		[setConnectionRemap]
	)

	const doImport2 = useCallback(() => {
		doImport(importPageNumber, pageNumber, connectionRemap)
	}, [doImport, importPageNumber, pageNumber, connectionRemap])

	const destinationGridSize = userConfig.properties?.gridSize

	const destinationGridRef = useRef<ButtonInfiniteGridRef>(null)
	const resetDestinationPosition = useCallback(() => {
		destinationGridRef.current?.resetPosition()
	}, [destinationGridRef])

	const sourceGridRef = useRef<ButtonInfiniteGridRef>(null)
	const resetSourcePosition = useCallback(() => {
		sourceGridRef.current?.resetPosition()
	}, [sourceGridRef])

	const isRunning = false

	const sourcePageInfo = isSinglePage ? snapshot.page : snapshot.pages?.[importPageNumber]
	const sourceGridSize = sourcePageInfo?.gridSize ?? destinationGridSize

	const [hasBeenRendered, hasBeenRenderedRef] = useHasBeenRendered()

	const [gridZoomController, gridZoomValue] = useGridZoom('import')

	return (
		<>
			<p>
				Choose a source page containing the buttons to import and a destination page where they will be imported. You
				can either replace an existing page or create a new one.
			</p>
			<Grid.Row className="import-page-row">
				<Grid.Col xs={12} xl={6} className="import-page-grid">
					<h5>Source Page</h5>
					<MyErrorBoundary>
						<>
							<Grid.Col sm={12}>
								<PageNumberPicker
									pageNumber={isSinglePage ? (snapshot.oldPageNumber ?? 1) : importPageNumber}
									changePage={isSinglePage ? undefined : changeImportPage}
									setPage={isSinglePage ? undefined : setImportPageNumber}
									pageOptions={snapshotPageOptions}
								>
									<Button color="light" title="Home Position" onClick={resetSourcePosition}>
										<FontAwesomeIcon icon={faHome} />
									</Button>
								</PageNumberPicker>
							</Grid.Col>
							<div className="buttongrid" ref={hasBeenRenderedRef}>
								{hasBeenRendered && sourceGridSize && (
									<ButtonInfiniteGrid
										ref={sourceGridRef}
										pageNumber={isSinglePage ? (snapshot.oldPageNumber ?? 1) : importPageNumber}
										gridSize={sourceGridSize}
										ButtonIconFactory={ButtonImportPreview}
										marquee={null}
										onHoverLocation={null}
										drawScale={gridZoomValue / 100}
										maxHeightToMatchCanvas
									/>
								)}
							</div>
						</>
					</MyErrorBoundary>
				</Grid.Col>

				<Grid.Col xs={12} xl={6} className="import-page-grid">
					<h5>Destination Page</h5>
					<MyErrorBoundary>
						<>
							<Grid.Col sm={12}>
								<ButtonGridHeader pageNumber={pageNumber} changePage={changePage} setPage={setPageNumber} newPageAtEnd>
									<ButtonGridZoomControl
										useCompactButtons={true}
										gridZoomValue={gridZoomValue}
										gridZoomController={gridZoomController}
									/>

									<Button color="light" className="ms-1" title="Home Position" onClick={resetDestinationPosition}>
										<FontAwesomeIcon icon={faHome} />
									</Button>
								</ButtonGridHeader>
							</Grid.Col>
							<div className="buttongrid">
								{hasBeenRendered && destinationGridSize && pageNumber != -1 && (
									<ButtonInfiniteGrid
										ref={destinationGridRef}
										pageNumber={pageNumber}
										gridSize={destinationGridSize}
										ButtonIconFactory={ButtonGridIcon}
										marquee={null}
										onHoverLocation={null}
										drawScale={gridZoomValue / 100}
										maxHeightToMatchCanvas
									/>
								)}
								{pageNumber === -1 && (
									<div
										style={{
											textAlign: 'center',
											fontSize: '1.5rem',
											marginTop: '5rem',
										}}
									>
										<FontAwesomeIcon icon={faFileCirclePlus} size="4x" />
										<p className="mt-1">The buttons will be imported to a new page.</p>
									</div>
								)}
							</div>
						</>
					</MyErrorBoundary>
				</Grid.Col>
			</Grid.Row>
			<MyErrorBoundary>
				<ImportRemap snapshot={snapshot} connectionRemap={connectionRemap} setConnectionRemap={setConnectionRemap2} />
			</MyErrorBoundary>
			<Callout color={pageNumber == -1 ? 'success' : 'warning'}>
				<h5>Import Buttons to Page</h5>
				<p>
					Clicking the button below will
					{pageNumber == -1
						? ' import the source page to a new page'
						: ' completely override the button on the existing destination page with the buttons on the selected source page'}
					.
				</p>
				<Button color={pageNumber == -1 ? 'success' : 'warning'} onClick={doImport2} disabled={isRunning}>
					<FontAwesomeIcon icon={pageNumber == -1 ? faFileCirclePlus : faFileCircleExclamation} />
					{pageNumber == -1 ? ' Import to new page' : ` Replace page ${pageNumber} with imported page`}
				</Button>
			</Callout>
		</>
	)
})

interface ImportRemapProps {
	snapshot: ClientImportObject
	connectionRemap: Record<string, string | undefined>
	setConnectionRemap: (fromId: string, toId: string) => void
}

export function ImportRemap({ snapshot, connectionRemap, setConnectionRemap }: ImportRemapProps): React.JSX.Element {
	const sortedConnections = useMemo(() => {
		if (!snapshot.connections) return []

		return Object.entries(snapshot.connections)
			.filter((ent) => !!ent[1])
			.sort(compareExportedInstances)
	}, [snapshot.connections])

	return (
		<div id="import_resolve">
			<h5>Import Connections Behavior</h5>
			<p>
				If you have existing connections that match the type of connections in the import, you can link them here.
				Otherwise, new connections will be created for any connections left unlinked. You can also choose to ignore
				certain connections if they are not needed.
			</p>
			<Table>
				<thead>
					<tr>
						<th>Behavior</th>
						<th>Import Connection Type</th>
						<th>Import Connection Name</th>
					</tr>
				</thead>
				<tbody>
					{sortedConnections.length === 0 && (
						<tr>
							<td colSpan={3}>No connections</td>
						</tr>
					)}
					{sortedConnections.map(([key, connection]) => (
						<ImportRemapRow
							key={key}
							id={key}
							connection={connection}
							connectionRemap={connectionRemap}
							setConnectionRemap={setConnectionRemap}
						/>
					))}
				</tbody>
			</Table>
		</div>
	)
}

interface ImportRemapRowProps {
	id: string
	connection: ClientImportObjectInstance
	connectionRemap: Record<string, string | undefined>
	setConnectionRemap: (fromId: string, toId: string) => void
}

const ImportRemapRow = observer(function ImportRemapRow({
	id,
	connection,
	connectionRemap,
	setConnectionRemap,
}: ImportRemapRowProps) {
	const { connections, modules } = useContext(RootAppStoreContext)

	const storeInfo = modules.getStoreInfo(ModuleInstanceType.Connection, connection.moduleId)
	const moduleInfo = modules.getModuleInfo(ModuleInstanceType.Connection, connection.moduleId)

	const moduleManifest = moduleInfo?.display ?? storeInfo

	const onChange = useCallback(
		(value: DropdownChoiceId) => setConnectionRemap(id, String(value)),
		[setConnectionRemap, id]
	)

	const selectOptions = useComputed(() => {
		const options: DropdownChoice[] = [
			{ id: '_blank', label: '[ Create new connection ]' },
			{ id: '_ignore', label: '[ Ignore ]' },
		]

		const currentConnections = connections.getAllOfModuleId(connection.moduleId)
		for (const conn of currentConnections) {
			options.push({
				id: conn.id,
				label: `Link to ${conn.label}`,
			})
		}
		return options
	}, [connections])

	return (
		<tr>
			<td>
				<SimpleDropdownInputField
					id={undefined} // TODO - link up with a label
					value={connectionRemap[id] ?? ''}
					setValue={onChange}
					choices={selectOptions}
				/>
			</td>
			<td>{moduleManifest?.name ?? `Unknown module (${connection.moduleId})`}</td>
			<td>{connection.label}</td>
		</tr>
	)
})

function ButtonImportPreview({ ...props }: ButtonInfiniteGridButtonProps) {
	const query = useQuery(
		trpc.importExport.controlPreview.queryOptions({
			location: {
				pageNumber: props.pageNumber,
				row: props.row,
				column: props.column,
			},
		})
	)

	return <ButtonGridIconBase {...props} image={query.data || null} />
}
